/**
 * Wat deze wijziging met de lopende dossiers doet — vóór het opslaan.
 *
 * Dit is de kern van het besluit van 12-09-2026. Niet een vinkje "ik weet het
 * zeker", maar een tabel met dossier, regeling, oud bedrag, nieuw bedrag en
 * verschil, en een knop waar het BEDRAG in staat. Een bevestiging zonder getal
 * is een bevestiging die je wegklikt.
 *
 * De tabel toont drie dingen naast elkaar, niet één. Een vergelijking op het
 * bedrag alléén miste twee gemeten gevolgen: `code` hernoemen haalt de
 * EIA/MIA-stapelwaarschuwing van alle vier de kaarten zonder dat er een euro
 * verandert, en `verplicht` omzetten tilt drie dossiers van "gedeeltelijk" naar
 * "volledig" — een badge die zegt dat er getoetst is terwijl dat niet zo is.
 *
 * De cijfers komen uit `subsidie-voorvertoning.ts`, die met de echte rekenkern
 * rekent — niet met een kopie ervan. Zie daar waarom dat het hele punt is.
 */
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, FileQuestion, Info, Sparkles } from 'lucide-react';
import {
  alsEuro, bevestigingsTekst, raaktBedrag, voorvertoningWaarschuwingen, type Voorvertoning,
} from '@/hooks/subsidie-voorvertoning';

export function VoorvertoningDialog({
  voorvertoning,
  titel,
  isBezig,
  onBevestig,
  onAnnuleer,
}: {
  /** `null` = dicht. */
  voorvertoning: Voorvertoning | null;
  /** Wat er wordt opgeslagen, in de taal van het scherm ("Het bedrag mia / categorie_b"). */
  titel: string;
  isBezig: boolean;
  onBevestig: () => void;
  onAnnuleer: () => void;
}) {
  if (!voorvertoning) return null;
  const v = voorvertoning;
  const waarschuwingen = voorvertoningWaarschuwingen(v);
  const metGeld = v.geraakt.filter(raaktBedrag);

  const kop =
    v.soort === 'nieuwe-regeling' ? 'Een nieuwe regeling is niet vooruit door te rekenen'
      : v.soort === 'verschil'
        ? (metGeld.length > 0
          ? `Dit verandert ${metGeld.length} ${metGeld.length === 1 ? 'dossier' : 'dossiers'}`
          : `Geen bedrag verandert, maar ${v.geraakt.length} ${v.geraakt.length === 1 ? 'dossier verandert' : 'dossiers veranderen'} van beoordeling`)
        : v.soort === 'geen-dossiers' ? 'Geen dossier om tegen te vergelijken'
          : 'Geen bedrag en geen beoordeling verandert';

  return (
    <AlertDialog open onOpenChange={o => { if (!o) onAnnuleer(); }}>
      <AlertDialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>{kop}</AlertDialogTitle>
          <AlertDialogDescription>{titel}</AlertDialogDescription>
        </AlertDialogHeader>

        {/*
          Een nieuwe regeling krijgt bewust GEEN tabel. Er valt niets door te
          rekenen — er staan nog geen regels en geen bedragen onder — en een
          lege tabel zou suggereren dat er wél gemeten is. De waarschuwingen
          hieronder zeggen in plaats daarvan wat er straks gebeurt.
        */}
        {v.soort === 'nieuwe-regeling' && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
            <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              <strong className="font-medium">Hier geldt de belofte "je ziet eerst wat het doet" niet.</strong>{' '}
              Deze regeling bestaat nog niet, dus er is niets om mee te vergelijken. Vanaf je eerste regel
              en je eerste bedrag zie je bij elke stap wél wat er verschuift.
            </span>
          </div>
        )}

        {v.soort === 'geen-dossiers' && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
            <FileQuestion className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              <strong className="font-medium">Er is vandaag geen enkel dossier om tegen te vergelijken.</strong>{' '}
              Dat is iets anders dan "geen verandering": er valt niets te meten, dus er is ook niets gemeten.
              Wat je hier wijzigt telt mee zodra het eerste dossier erbij komt.
            </span>
          </div>
        )}

        {v.soort === 'geen-verschil' && (
          <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Alle {v.bekeken} {v.bekeken === 1 ? 'dossier is' : 'dossiers zijn'} opnieuw doorgerekend met de
              echte rekenkern: geen bedrag, geen beoordeling en geen waarschuwing verschuift.
            </span>
          </div>
        )}

        {waarschuwingen.map(w => (
          <div key={w} className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{w}</span>
          </div>
        ))}

        {v.geraakt.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="p-1.5">Dossier</th>
                  <th className="p-1.5">Regeling</th>
                  <th className="p-1.5 text-right">Nu</th>
                  <th className="p-1.5 text-right">Wordt</th>
                  <th className="p-1.5 text-right">Verschil</th>
                  <th className="p-1.5">Beoordeling</th>
                </tr>
              </thead>
              <tbody>
                {v.geraakt.map(g => (
                  <tr key={`${g.intakeId}-${g.regelingCode}`} className="border-t align-top">
                    <td className="p-1.5">{g.dossier}</td>
                    <td className="p-1.5 text-muted-foreground">{g.regelingCode}</td>
                    <td className="p-1.5 text-right tabular-nums">{alsEuro(g.oud)}</td>
                    <td className="p-1.5 text-right tabular-nums font-medium">{alsEuro(g.nieuw)}</td>
                    <td
                      className={`p-1.5 text-right tabular-nums font-medium ${
                        g.verschil > 0 ? 'text-red-700' : g.verschil < 0 ? 'text-emerald-700' : 'text-muted-foreground'
                      }`}
                    >
                      {g.verschil === 0 ? '—' : `${g.verschil > 0 ? '+' : '−'}${alsEuro(Math.abs(g.verschil))}`}
                    </td>
                    <td className="p-1.5 text-xs">
                      {g.oudeStatus !== g.nieuweStatus && (
                        <div className="font-medium">{g.oudeStatus} → {g.nieuweStatus}</div>
                      )}
                      {g.verdwenenAdviezen.map(t => (
                        <div key={t} className="text-red-700">− {t}</div>
                      ))}
                      {g.nieuweAdviezen.map(t => (
                        <div key={t} className="text-emerald-700">+ {t}</div>
                      ))}
                      {g.oudeStatus === g.nieuweStatus
                        && g.verdwenenAdviezen.length === 0
                        && g.nieuweAdviezen.length === 0
                        && <span className="text-muted-foreground">ongewijzigd</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="pt-2 text-xs text-muted-foreground">
              Doorgerekend over {v.bekeken} {v.bekeken === 1 ? 'dossier' : 'dossiers'} met dezelfde rekenkern
              die de offerte gebruikt. Een bedrag dat omhooggaat belandt via de dossier-tab in een
              businesscase en uiteindelijk in een klantofferte; een verdwenen waarschuwing verdwijnt daar ook.
            </p>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isBezig}>Annuleren</AlertDialogCancel>
          <AlertDialogAction
            disabled={isBezig}
            onClick={onBevestig}
            className={v.onomkeerbaar ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
          >
            {bevestigingsTekst(v)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
